import React from 'react';
import { translate, InjectedTranslateProps } from 'react-i18next';
import {
  notifyViewFinishedLoading,
  hideSidebar,
  buildPageComponentLink,
  ExternalPageId,
  PageComponentId,
  navigateTo,
  INavigateToPageConfig,
} from '@wix/business-manager-api';
import { create } from '@wix/fedops-logger';
import { Modal, MessageBoxFunctionalLayout } from 'wix-style-react';
import { Sidebar } from '../Sidebar';
import { Viewer } from '../Viewer';
import { FilterType } from '../Filter';
import { Revision, EditorType, SwitchType } from '../../types';
import { COMPONENT_NAME, IBMModuleParams } from '../../config';
import { revisionFixtures } from './fixtures'; // TODO remove after DEV DONE
import {
  fetchRevisions,
  fetchMetaSiteData,
  restoreRevision,
  renameRevision,
  starRevision,
  unStarRevision,
} from '../../api/revision';
import {
  getRevisionId,
  getRevisionLink,
  getEditorLink,
  getAdiLink,
} from '../../helpers';
import { BiLogger } from '../../bi';

import styles from './App.scss';

interface IAppProps extends InjectedTranslateProps, IBMModuleParams {}

interface IAppState {
  editorLink: string;
  revisionLink: string;
  storyId: string;
  siteId: string;
  revisionList: Revision[];
  initialRevisionList: Revision[];
  filterOption: FilterType;
  activeRevision: string;
  currentRevision: string;
  isRestoreRegularModalOpen: boolean;
  isRestoreAdiToAdiModalOpen: boolean;
  isRestoreEditorToAdiModalOpen: boolean;
  isRestoreAdiToEditorModalOpen: boolean;
  isSuccessRestoreAdiModalOpen: boolean;
  isSuccessRestoreModalOpen: boolean;
  isErrorRestoreModalOpen: boolean;
  isSeparateRevisions: boolean;
  hasReleaseCandidate: boolean;
  isBackToDashboard: boolean;
}

class App extends React.Component<IAppProps, IAppState> {
  handleRestoreAction = async (targetRevision: Revision) => {
    this.targetRevision = targetRevision;

    if (
      !(
        this.state.initialRevisionList[0].editorType !==
        targetRevision.editorType
      )
    ) {
      this.switchType = 'regular';
    } else if (targetRevision.editorType === 'adi') {
      this.switchType = 'editor to adi';
    } else {
      this.switchType = 'adi to editor';
    }

    this.biLogger.reportRestoreRevisionClicked(
      targetRevision.revision,
      new Date(targetRevision.dateCreated),
      targetRevision.editorType,
      this.switchType,
    );

    if (
      !this.state.isSeparateRevisions ||
      (this.switchType === 'regular' && targetRevision.editorType === 'editor')
    ) {
      this.setState({
        isRestoreRegularModalOpen: true,
      });
    } else if (
      this.switchType === 'regular' &&
      targetRevision.editorType === 'adi'
    ) {
      this.setState({
        isRestoreAdiToAdiModalOpen: true,
      });
    } else if (this.switchType === 'editor to adi') {
      this.setState({
        isRestoreEditorToAdiModalOpen: true,
      });
    } else {
      this.setState({
        isRestoreAdiToEditorModalOpen: true,
      });
    }
  };
  handleRestoreModalCancel = () => {
    if (this.targetRevision && this.switchType) {
      this.biLogger.reportCancelRestoreClicked(
        this.targetRevision.revision,
        new Date(this.targetRevision.dateCreated),
        this.targetRevision.editorType,
        this.switchType,
      );
    }

    this.handleRestoreModalClose();
  };
  handleRestoreConfirmationAction = async () => {
    if (this.targetRevision && this.switchType) {
      this.biLogger.reportConfirmRestoreClicked(
        this.targetRevision.revision,
        new Date(this.targetRevision.dateCreated),
        this.targetRevision.editorType,
        this.switchType,
      );
    }
    const revisionRestoreStart = Date.now();

    const restoreRevisionResult = await restoreRevision(
      this.state.siteId,
      this.state.activeRevision,
    );

    this.handleRestoreModalClose();

    if (!(restoreRevisionResult instanceof Error)) {
      if (this.targetRevision && this.switchType) {
        this.biLogger.reportRestoreSuccess(
          this.targetRevision.revision,
          new Date(this.targetRevision.dateCreated),
          this.targetRevision.editorType,
          Date.now() - revisionRestoreStart,
          this.switchingBuilders(),
        );
      }
      const showAdiMessage =
        this.state.isSeparateRevisions &&
        this.state.activeRevision.endsWith('adi');
      if (showAdiMessage) {
        this.setState({
          isSuccessRestoreAdiModalOpen: true,
          editorLink: getAdiLink(
            this.state.siteId,
            this.state.storyId,
            this.props.metaSiteId,
          ),
        });
      } else {
        this.setState({
          isSuccessRestoreModalOpen: true,
          editorLink: getEditorLink(
            this.state.siteId,
            this.state.storyId,
            this.props.metaSiteId,
          ),
        });
      }
    } else {
      if (this.targetRevision && this.switchType) {
        this.biLogger.reportRestoreFailed(
          this.targetRevision.revision,
          new Date(this.targetRevision.dateCreated),
          this.targetRevision.editorType,
          this.switchingBuilders(),
        );
      }
      this.setState({
        isErrorRestoreModalOpen: true,
      });
    }
  };
  handleSuccessRestoreModalAction = async () => {
    // Should I use react-router ... or js way like
    if (this.targetRevision) {
      this.biLogger.reportGoToEditorClicked(
        this.targetRevision.revision,
        new Date(this.targetRevision.dateCreated),
      );
    }
    window.open(this.state.editorLink, '_blank');
    await this.handleSuccessRestoreModalClose();
  };
  handleSuccessRestoreModalCancel = async () => {
    if (this.targetRevision && this.switchType) {
      this.biLogger.reportStayInPageClicked(
        this.targetRevision.revision,
        new Date(this.targetRevision.dateCreated),
      );
    }
    await this.handleSuccessRestoreModalClose();
  };
  //All use SiteId + revisionNumber + editorType
  handleStartAction = async (revision: Revision, flag: boolean) => {
    this.biLogger.reportRevisionStarred(
      revision.revision,
      new Date(revision.dateCreated),
      revision.editorType,
    );

    try {
      if (flag) {
        await starRevision(this.state.siteId, revision);
      } else {
        await unStarRevision(this.state.siteId, revision);
      }
      const flaggedState = [...this.state.initialRevisionList];
      const flagRevisionIndex = flaggedState.findIndex(
        (revisionItem) => revisionItem === revision,
      );
      flaggedState[flagRevisionIndex].isStarred = flag;
    } catch (e) {
      console.log('----- POST start/unstar revision error ------');
      console.log(JSON.stringify(e));
    }
  };

  handleRestoreModalClose = () => {
    this.setState({
      isRestoreRegularModalOpen: false,
      isRestoreAdiToAdiModalOpen: false,
      isRestoreEditorToAdiModalOpen: false,
      isRestoreAdiToEditorModalOpen: false,
    });
  };
  handleRenameAction = async (revision: Revision, newName: string) => {
    try {
      newName = newName.trim();
      if (newName.length > 0) {
        this.biLogger.reportRevisionRenamed(
          revision.revision,
          new Date(revision.dateCreated),
        );
        await renameRevision(this.state.siteId, revision, newName);
      }
    } catch (e) {
      console.log(JSON.stringify(e));
    }
  };
  handleRevisionView = (revision: Revision) => {
    this.biLogger.reportViewRevisionClicked(
      revision.revision,
      new Date(revision.dateCreated),
      revision.editorType,
    );

    this.setState({
      activeRevision: getRevisionId(revision),
      revisionLink: this.getRevisionLink(revision),
    });
  };

  handleErrorRestoreModalClose = () => {
    this.setState({
      isErrorRestoreModalOpen: false,
    });
  };
  handleSuccessRestoreModalClose = async () => {
    this.setState({
      isSuccessRestoreAdiModalOpen: false,
      isSuccessRestoreModalOpen: false,
    });

    await this.getRevisionList();
  };
  switchingBuilders = () => {
    return !(
      this.targetRevision &&
      this.state.currentRevision.endsWith(this.targetRevision.editorType)
    );
  };
  handleIframeLoaded = () => {
    if (
      this.activeRevision &&
      this.activeRevision.editorType === 'adi' &&
      this.revisionSelectStart
    ) {
      this.biLogger.reportViewSuccess(
        this.activeRevision.revision,
        new Date(this.activeRevision.dateCreated),
        this.activeRevision.editorType,
        window.performance.now() - this.revisionSelectStart,
      );
    }
  };

  getRevisionLink = (revision: Revision) => {
    this.activeRevision = revision;
    this.revisionSelectStart = window.performance.now();

    return getRevisionLink(
      this.state.siteId,
      this.state.storyId,
      this.props.metaSiteId,
      revision,
    );
  };

  handleFilterChange = (filter: FilterType) => {
    this.setState({
      filterOption: filter,
    });

    this.biLogger.reportRevisionsFiltered(filter);

    const filteredRevisions = this.state.initialRevisionList.filter(
      (revision) => {
        switch (filter) {
          case 'published':
            return revision.isPublished === true;
          case 'manually saved':
            return (
              revision.autosaved === false &&
              revision.isPublished === false &&
              revision.isReleaseCandidate === false
            );
          case 'starred':
            return revision.isStarred === true;
          case 'release candidate':
            return revision.isReleaseCandidate === true;
          default:
            return true;
        }
      },
    );

    this.setState({
      revisionList: filteredRevisions,
    });
  };
  getRevisionList = async () => {
    hideSidebar();
    let revisionList = await fetchRevisions(
      `${this.state.siteId}/${this.props.metaSiteId}`,
    );

    // TODO better error handling
    if (!(revisionList instanceof Error)) {
      this.setState({
        revisionList,
        initialRevisionList: revisionList,
        hasReleaseCandidate: revisionList.some(
          (revision) => revision.isReleaseCandidate,
        ),
      });
    } else {
      // TODO remove it after DEV DONE
      revisionList = [];
      this.setState({
        initialRevisionList: [],
        revisionList: [],
      });
    }

    const firstRevision = revisionList[0];
    this.revisionSelectStart = window.performance.now();
    this.setState({
      activeRevision: getRevisionId(firstRevision),
      currentRevision: getRevisionId(firstRevision),
    });
  };

  private readonly biLogger = new BiLogger(this.props.metaSiteId);
  private revisionSelectStart: number | undefined;
  private targetRevision: Revision | undefined;
  private activeRevision: Revision | undefined;
  private switchType: SwitchType | undefined;
  private readonly backToDashboard = () =>
    navigateTo({ pageComponentId: PageComponentId.SiteSettings });

  constructor(props: IAppProps) {
    super(props);
    this.state = {
      editorLink: '',
      revisionLink: '',
      storyId: '',
      siteId: '',
      revisionList: [],
      initialRevisionList: [],
      filterOption: 'all',
      activeRevision: '',
      currentRevision: '',
      isRestoreRegularModalOpen: false,
      isRestoreAdiToAdiModalOpen: false,
      isRestoreEditorToAdiModalOpen: false,
      isRestoreAdiToEditorModalOpen: false,
      isSuccessRestoreAdiModalOpen: false,
      isSuccessRestoreModalOpen: false,
      isErrorRestoreModalOpen: false,
      isSeparateRevisions: false,
      hasReleaseCandidate: false,
      isBackToDashboard: false,
    };

    this.biLogger.reportPagePerformance();
    this.biLogger.reportPageStart();

    window.addEventListener(
      'message',
      (m) => {
        if (
          m.data === 'adiRevisionReloaded' ||
          m.data === 'santaReady' ||
          m.data === 'viewerReady'
        ) {
          const iframe = window.document.querySelector(
            'iframe[data-hook="preview-iframe"]',
          ) as any;
          if (
            m.source === iframe.contentWindow &&
            this.activeRevision &&
            this.revisionSelectStart
          ) {
            this.biLogger.reportViewSuccess(
              this.activeRevision.revision,
              new Date(this.activeRevision.dateCreated),
              this.activeRevision.editorType,
              window.performance.now() - this.revisionSelectStart,
            );
          }
        }
      },
      false,
    );
  }

  async componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const referralInfo = urlParams.get('referralInfo');
    if (referralInfo && referralInfo === 'site-settings') {
      this.setState({
        isBackToDashboard: true,
      });
    }
    // Note: you might want to invoke notify after initial data fetch (to keep BM loader during fetch)
    const fedopsLogger = create(COMPONENT_NAME);
    fedopsLogger.appLoaded();
    notifyViewFinishedLoading(COMPONENT_NAME);
    const startTime = Date.now();

    hideSidebar();
    // TODO update with real site revision
    const metaSiteData = await fetchMetaSiteData(this.props.metaSiteId);

    if (!(metaSiteData instanceof Error)) {
      this.setState({
        siteId: metaSiteData.siteId,
        storyId: metaSiteData.storyId,
        isSeparateRevisions: metaSiteData.isSeparateRevisions,
      });
    }

    await this.getRevisionList();

    const adiRevisions = this.state.revisionList.filter(
      (r) => r.editorType === 'adi',
    ).length;
    const editorRevisions = this.state.revisionList.filter(
      (r) => r.editorType === 'editor',
    ).length;
    this.biLogger.reportPageLoadComplete(
      Date.now() - startTime,
      window.performance.now(),
      adiRevisions,
      editorRevisions,
    );

    this.setState((state) => ({
      revisionLink: this.getRevisionLink(state.initialRevisionList[0]),
    }));
  }

  render() {
    hideSidebar();

    const { t } = this.props;
    const {
      filterOption,
      revisionList,
      activeRevision,
      currentRevision,
      hasReleaseCandidate,
      isRestoreRegularModalOpen,
      isRestoreAdiToAdiModalOpen,
      isRestoreEditorToAdiModalOpen,
      isRestoreAdiToEditorModalOpen,
      isSuccessRestoreAdiModalOpen,
      isSuccessRestoreModalOpen,
      isErrorRestoreModalOpen,
      isSeparateRevisions,
      isBackToDashboard,
    } = this.state;

    const SUPPORT_LINK = `${buildPageComponentLink({
      pageComponentId: ExternalPageId.Support,
      contextData: {
        article: '/article/managing-your-site-history-1079227',
        locale: this.props.accountLanguage,
      },
    })}?referral=MAD_history_help`;

    return (
      <>
        <div className={styles.root}>
          <div className={styles.sidebar}>
            <Sidebar
              t={t}
              onBackToDashboard={this.backToDashboard}
              isBackToDashboard={isBackToDashboard}
              supportLink={SUPPORT_LINK}
              isSeparateRevisions={isSeparateRevisions}
              filterOption={filterOption}
              hasReleaseCandidate={hasReleaseCandidate}
              revisionList={revisionList}
              activeRevision={activeRevision}
              currentRevision={currentRevision}
              onView={this.handleRevisionView}
              onRestore={this.handleRestoreAction}
              onFilterChange={this.handleFilterChange}
              onStar={this.handleStartAction}
              onRename={this.handleRenameAction}
            />
          </div>
          <div className={styles.preview}>
            <Viewer
              revisionLink={this.state.revisionLink}
              onLoad={this.handleIframeLoaded}
            />
          </div>
        </div>
        <Modal
          isOpen={isRestoreRegularModalOpen}
          onRequestClose={this.handleRestoreModalClose}
        >
          <MessageBoxFunctionalLayout
            dataHook="before-restore-revision-modal"
            confirmText={t('DIALOG_BEFORE_RESTORE_CONFIRM')}
            cancelText={t('DIALOG_BEFORE_RESTORE_CANCEL')}
            onOk={this.handleRestoreConfirmationAction}
            onCancel={this.handleRestoreModalCancel}
            onClose={this.handleRestoreModalClose}
            title={t('DIALOG_BEFORE_RESTORE_TITLE')}
            closeButton
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t('DIALOG_BEFORE_RESTORE_CONTENT'),
              }}
            />
          </MessageBoxFunctionalLayout>
        </Modal>
        <Modal
          isOpen={isRestoreAdiToAdiModalOpen}
          onRequestClose={this.handleRestoreModalClose}
        >
          <MessageBoxFunctionalLayout
            dataHook="before-restore-revision-adi-to-adi-modal"
            confirmText={t('DIALOG_BEFORE_RESTORE_CONFIRM_ADI_TO_ADI')}
            cancelText={t('DIALOG_BEFORE_RESTORE_CANCEL_ADI_TO_ADI')}
            onOk={this.handleRestoreConfirmationAction}
            onCancel={this.handleRestoreModalCancel}
            onClose={this.handleRestoreModalClose}
            title={t('DIALOG_BEFORE_RESTORE_TITLE_ADI_TO_ADI')}
            closeButton
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t('DIALOG_BEFORE_RESTORE_CONTENT_ADI_TO_ADI'),
              }}
            />
          </MessageBoxFunctionalLayout>
        </Modal>

        <Modal
          isOpen={isRestoreEditorToAdiModalOpen}
          onRequestClose={this.handleRestoreModalClose}
        >
          <MessageBoxFunctionalLayout
            dataHook="before-restore-revision-editor-to-adi-modal"
            confirmText={t('DIALOG_BEFORE_RESTORE_CONFIRM_EDITOR_TO_ADI')}
            cancelText={t('DIALOG_BEFORE_RESTORE_CANCEL_EDITOR_TO_ADI')}
            onOk={this.handleRestoreConfirmationAction}
            onCancel={this.handleRestoreModalCancel}
            onClose={this.handleRestoreModalClose}
            title={t('DIALOG_BEFORE_RESTORE_TITLE_EDITOR_TO_ADI')}
            closeButton
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t('DIALOG_BEFORE_RESTORE_CONTENT_EDITOR_TO_ADI'),
              }}
            />
          </MessageBoxFunctionalLayout>
        </Modal>

        <Modal
          isOpen={isRestoreAdiToEditorModalOpen}
          onRequestClose={this.handleRestoreModalClose}
        >
          <MessageBoxFunctionalLayout
            dataHook="before-restore-revision-adi-to-editor-modal"
            confirmText={t('DIALOG_BEFORE_RESTORE_CONFIRM_ADI_TO_EDITOR')}
            cancelText={t('DIALOG_BEFORE_RESTORE_CANCEL_ADI_TO_EDITOR')}
            onOk={this.handleRestoreConfirmationAction}
            onCancel={this.handleRestoreModalCancel}
            onClose={this.handleRestoreModalClose}
            title={t('DIALOG_BEFORE_RESTORE_TITLE_ADI_TO_EDITOR')}
            closeButton
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t('DIALOG_BEFORE_RESTORE_CONTENT_ADI_TO_EDITOR'),
              }}
            />
          </MessageBoxFunctionalLayout>
        </Modal>

        <Modal
          isOpen={isSuccessRestoreModalOpen}
          onRequestClose={this.handleSuccessRestoreModalClose}
        >
          <MessageBoxFunctionalLayout
            dataHook="after-restore-revision-modal"
            confirmText={t('DIALOG_AFTER_RESTORE_CONFIRM')}
            cancelText={t('DIALOG_AFTER_RESTORE_CANCEL')}
            onOk={this.handleSuccessRestoreModalAction}
            onCancel={this.handleSuccessRestoreModalCancel}
            onClose={this.handleSuccessRestoreModalClose}
            title={t('DIALOG_AFTER_RESTORE_TITLE')}
            closeButton
          >
            {t('DIALOG_AFTER_RESTORE_CONTENT')}
          </MessageBoxFunctionalLayout>
        </Modal>

        <Modal
          isOpen={isSuccessRestoreAdiModalOpen}
          onRequestClose={this.handleSuccessRestoreModalClose}
        >
          <MessageBoxFunctionalLayout
            dataHook="after-restore-revision-adi-modal"
            confirmText={t('DIALOG_AFTER_RESTORE_CONFIRM_ADI')}
            cancelText={t('DIALOG_AFTER_RESTORE_CANCEL_ADI')}
            onOk={this.handleSuccessRestoreModalAction}
            onCancel={this.handleSuccessRestoreModalCancel}
            onClose={this.handleSuccessRestoreModalClose}
            title={t('DIALOG_AFTER_RESTORE_TITLE_ADI')}
            closeButton
          >
            {t('DIALOG_AFTER_RESTORE_CONTENT_ADI')}
          </MessageBoxFunctionalLayout>
        </Modal>

        <Modal
          isOpen={isErrorRestoreModalOpen}
          onRequestClose={this.handleErrorRestoreModalClose}
        >
          <MessageBoxFunctionalLayout
            dataHook="error-restore-revision-modal"
            confirmText={t('DIALOG_ERROR_RESTORE_CONFIRM')}
            onOk={this.handleErrorRestoreModalClose}
            onClose={this.handleErrorRestoreModalClose}
            title={t('DIALOG_ERROR_RESTORE_TITLE')}
            closeButton
            theme="red"
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t('DIALOG_ERROR_RESTORE_CONTENT', { SUPPORT_LINK }),
              }}
            />
          </MessageBoxFunctionalLayout>
        </Modal>
      </>
    );
  }
}

export default translate()(App);
